import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { KojinResult } from 'interface/kojin'
import { TextMatch } from 'shared/text'
import { KojinSearchFlags, KojinListSortKey } from '../types'

type useKojinListProps = {
  searchFlags: KojinSearchFlags,
  kojinList: KojinResult[]
}
export const useKojinList = ({
  searchFlags,
  kojinList
}: useKojinListProps) => {
  const inputTimer = useRef<NodeJS.Timeout | null>(null)

  const [searchWord, setSearchWord] = useState<string>('')
  const [orderBy, setOrderBy] = useState<KojinListSortKey>('total')
  const [showUnMatch, setShowUnMatch] = useState<boolean>(false)

  const [modal, setModal] = useState<'authorGraph' | null>(null)

  const [selectedKojin, setSelectedKojin] = useState<KojinResult | null>(null)

  useEffect(
    () => {
      if (searchFlags.hasExtend) {
        setOrderBy('rate')
      } else if (searchFlags.hasJournal) {
        setOrderBy('journal')
      } else if (searchFlags.hasTargetList) {
        setOrderBy('sequence')
      }
    },
    [searchFlags, setOrderBy]
  )
  const onSelectKojin = useCallback(
    (kojinCode: string | null) => {
      setSelectedKojin(kojinCode ? (kojinList?.find(kojin => kojin.kojinCode === kojinCode) ?? null) : null)
    },
    [setSelectedKojin, kojinList]
  )
  const onSelectAuthor = useCallback(
    (authorId: number | null) => {
      setSelectedKojin(authorId ? (kojinList?.find(kojin => kojin.authorId === authorId) ?? null) : null)
    },
    [setSelectedKojin, kojinList]
  )
  const compaireRecord = useCallback(
    (a: KojinResult, b: KojinResult) => {
      switch (orderBy) {
        case 'total': {
          return (a.score?.rank ?? 999999) - (b.score?.rank ?? 999999)
        }
        case 'journal': {
          return (
            (a.score?.journal?.rank ?? 999999) -
            (b.score?.journal?.rank ?? 999999)
          )
        }
        case 'dpc': {
          return (a.score?.dpc?.rank ?? 999999) - (b.score?.dpc?.rank ?? 999999)
        }
        case 'cancer': {
          return (
            (a.score?.cancer?.rank ?? 999999) -
            (b.score?.cancer?.rank ?? 999999)
          )
        }
        case 'name': {
          return (a.isiNameKana ?? '').localeCompare(b.isiNameKana ?? '')
        }
        case 'yakusyoku': {
          const positionRankA =
            a.organ?.reduce(
              (s, organ) => Math.min(s, organ.positionRank ?? 0),
              9999
            ) ?? 9999
          const positionRankB =
            b.organ?.reduce(
              (s, organ) => Math.min(s, organ.positionRank ?? 0),
              9999
            ) ?? 9999
          return positionRankA - positionRankB
        }
        case 'rate': {
          return (b.target?.rate ?? 0) - (a.target?.rate ?? 0)
        }
        case 'sequence': {
          return (a.target?.sequence ?? 0) - (b.target?.sequence ?? 0)
        }
        default: {
          return (a.authorId ?? 0) - (b.authorId ?? 0)
        }
      }
    },
    [orderBy]
  )
  const filteredList = useMemo<KojinResult[]>(() => {
    const match = searchWord !== '' ? new TextMatch(searchWord) : undefined
    const filteredList = kojinList.filter((kojin) => {
      if (!showUnMatch && !kojin.kojinCode) return false
      if (match && !match.match(kojin.kojinCode, kojin.isiName, kojin.isiNameKana)) return false
      return true
    })
    filteredList.sort(compaireRecord)
    return filteredList
  }, [kojinList, showUnMatch, searchWord, compaireRecord])

  const onChangeSearchWord = useCallback(
    (searchWord: string) => {
      if (inputTimer.current) clearTimeout(inputTimer.current)
      inputTimer.current = setTimeout(() => {
        setSearchWord(searchWord)
        inputTimer.current = null
      }, 100)
    },
    [setSearchWord]
  )
  const onChangeOrderBy = useCallback(
    (value: KojinListSortKey) => setOrderBy(value),
    [setOrderBy]
  )
  const onChangeShowUnMatch = useCallback(
    (value: boolean) => setShowUnMatch(value),
    [setShowUnMatch]
  )
  const openAuthorGraphDialog = useCallback(
    () => setModal('authorGraph'),
    [setModal]
  )
  const closeModal = useCallback(
    () => setModal(null),
    [setModal]
  )

  return useMemo(
    () => ({
      filteredList,
      searchWord,
      onChangeSearchWord,
      orderBy,
      onChangeOrderBy,
      showUnMatch,
      onChangeShowUnMatch,
      modal,
      openAuthorGraphDialog,
      closeModal,
      selectedKojin,
      onSelectKojin,
      onSelectAuthor
      }),
    [
      filteredList,
      searchWord,
      onChangeSearchWord,
      orderBy,
      onChangeOrderBy,
      showUnMatch,
      onChangeShowUnMatch,
      modal,
      openAuthorGraphDialog,
      closeModal,
      selectedKojin,
      onSelectKojin,
      onSelectAuthor
    ]
  )
}
